import { supabase, callFunction } from "./supabase";
import { asAppError } from "./entitlements";
import type {
  AuditEvent, BrandDetail, Connection, ContentDraft, Delivery, DraftKind, DraftSource,
  MediaAsset, MediaFolder, Post,
  Platform, PostKind, ValidationResult, YouTubePrivacy,
} from "./types";
import { checksum, makePreview, type MediaFacts } from "./media";
import type { Dashboard as DashboardData } from "../dashboard/types";

/**
 * The workspace the user is acting in.
 *
 * This used to be "the first membership row", which silently picks a business
 * for anyone who works for two (ADR-0011). It now honours the explicit choice
 * the switcher remembers, and falls back to the first only when there is no
 * choice or the remembered one is no longer theirs. The server verifies the
 * workspace on every call regardless — this is convenience, not authorization.
 */
export async function currentWorkspace(): Promise<string> {
  const { data, error } = await supabase
    .from("workspace_members")
    .select("workspace_id");
  if (error) throw asAppError(error);

  const ids = (data ?? []).map((r) => r.workspace_id as string);
  if (ids.length === 0) throw new Error("No workspace found for this account");

  let remembered: string | null = null;
  try {
    remembered = localStorage.getItem("simino.workspace");
  } catch {
    // Blocked storage: the first workspace is the honest fallback.
  }
  return remembered && ids.includes(remembered) ? remembered : ids[0];
}

export async function listConnections(workspaceId: string): Promise<Connection[]> {
  const { data, error } = await supabase
    .from("social_connections")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("platform");
  if (error) throw new Error(error.message);
  return data as Connection[];
}

/**
 * Connection attempts, successful and failed, newest first. A failed OAuth used
 * to leave nothing behind but a query parameter, so there was no way to answer
 * "I authorised it, why is nothing connected?" from inside the app.
 */
export async function listConnectionActivity(
  workspaceId: string,
  limit = 10,
): Promise<AuditEvent[]> {
  const { data, error } = await supabase
    .from("audit_events")
    .select("id, action, target_type, target_id, metadata, created_at")
    .eq("workspace_id", workspaceId)
    .in("action", ["connection.created", "connection.failed"])
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return data as AuditEvent[];
}

export async function startOAuth(platform: string, workspaceId: string): Promise<string> {
  const { authorize_url } = await callFunction<{ authorize_url: string }>("oauth-start", {
    platform,
    workspace_id: workspaceId,
    redirect_to: `${window.location.origin}/connections`,
  });
  return authorize_url;
}

/**
 * Disconnecting stops publication and is audited, so it is an RPC rather than an
 * UPDATE: the table has no write policy for clients any more (ADR-0011).
 */
export async function disconnect(connectionId: string): Promise<void> {
  const { error } = await supabase.rpc("disconnect_connection", {
    p_connection_id: connectionId,
  });
  if (error) throw asAppError(error);
}

export async function validateMedia(
  media: MediaFacts,
  platforms: string[],
  kind: PostKind,
): Promise<{ results: ValidationResult[]; publishable: boolean }> {
  return callFunction("validate-media", { media, platforms, kind });
}

/**
 * The same verdict for several files at once, keyed by whatever id the caller
 * gave them.
 *
 * Compose needs this because a carousel is validated photo by photo — Instagram
 * rejects the whole post for one PNG in slot four [C008] — and doing that with
 * one request per photo would mean ten round trips every time somebody ticks an
 * account. The batch shape already existed for the library's fit badges; this is
 * the same endpoint with the caller's own ids.
 */
export async function validateMediaItems(
  items: Array<{ id: string; media: MediaFacts; kind: PostKind }>,
  platforms: string[],
): Promise<Map<string, ValidationResult[]>> {
  const payload = await callFunction<{ items: Array<{ id: string; results: ValidationResult[] }> }>(
    "validate-media",
    { items, platforms },
  );
  return new Map(payload.items.map((r) => [r.id, r.results]));
}

/** Chunk size for the batch below — well under the function's 200-item ceiling. */
const FIT_CHUNK = 100;

/**
 * The same verdict as `validateMedia`, for a page of the library at once.
 *
 * A fit badge on every tile is only worth having if it is free: one round trip
 * per tile would make the library slower the fuller it gets. So the whole page
 * goes in one request, against the same rules read once on the other side.
 *
 * Facts come from the stored row, not from the bytes — width, height, duration
 * and size were measured at upload (ADR-0004) and there is nothing here to
 * re-measure. A row that predates a measurement has nulls, which the validator
 * reports as "cannot check" rather than a pass.
 */
export async function mediaFit(
  assets: Array<Pick<MediaAsset, "id" | "mime_type" | "byte_size" | "width" | "height" | "duration_seconds">>,
  platforms?: Platform[],
): Promise<Map<string, ValidationResult[]>> {
  const out = new Map<string, ValidationResult[]>();
  for (let i = 0; i < assets.length; i += FIT_CHUNK) {
    const items = assets.slice(i, i + FIT_CHUNK).map((a) => ({
      id: a.id,
      kind: (a.mime_type.startsWith("video/") ? "video" : "photo") as PostKind,
      media: {
        mimeType: a.mime_type,
        byteSize: a.byte_size,
        width: a.width,
        height: a.height,
        durationSeconds: a.duration_seconds,
      },
    }));
    const payload = await callFunction<{ items: Array<{ id: string; results: ValidationResult[] }> }>(
      "validate-media",
      platforms?.length ? { items, platforms } : { items },
    );
    for (const row of payload.items ?? []) out.set(row.id, row.results);
  }
  return out;
}

/**
 * Why a file was turned away, in terms of what to do about it.
 *
 * "Could not make a thumbnail" is true and unhelpful. The two real causes look
 * nothing alike from the outside: an image this browser cannot decode is
 * usually a format thing and converting solves it, while a video is almost
 * always a missing codec — Safari and Chrome ship different ones, which is why
 * naming the browser is the useful advice rather than a platitude.
 */
function noThumbnailMessage(file: File): string {
  return file.type.startsWith("video/")
    ? "This browser could not read a frame from that video, so it cannot be added to the " +
      "library. That is usually a codec it does not ship — Safari and Chrome support different " +
      "ones, so another browser may take it. The phone app will also accept it."
    : "This browser could not make a thumbnail for that image, so it cannot be added to the " +
      "library. Converting it to JPEG or PNG first will fix it.";
}

export async function uploadMedia(
  workspaceId: string,
  file: File,
  facts: MediaFacts,
  /** Where it is filed (ADR-0018). Null is the workspace root. The bytes land
   *  in the same flat, UUID-keyed place either way. */
  folderId: string | null = null,
  /** Whose it is (ADR-0029). Null lets the database fill it in when the
   *  workspace has exactly one brand, and leave it unfiled when it does not. */
  brandId: string | null = null,
): Promise<string> {
  // File size and storage are enforced by a trigger on media_assets, which
  // fires *after* the bytes are already in the bucket. Asking first turns a
  // refused 4 GB upload into an instant answer instead of a long wait followed
  // by an error and an orphaned object.
  await assertUploadAllowed(workspaceId, file, facts);

  const ext = file.name.split(".").pop() ?? "bin";
  // First path segment is the tenant — the storage RLS policy keys on it.
  const path = `${workspaceId}/${crypto.randomUUID()}.${ext}`;

  // Made BEFORE the original goes up, and now a precondition of it.
  //
  // Every asset has a derivative, and this is where that is kept true on this
  // client. `database/scripts/media_preview_required.sql` makes it a database
  // rule too, once its one remaining caller is fixed.
  //
  // Asked before a byte moves: finding out after pushing a 200 MB video would
  // be the worst possible order, and refusing here leaves nothing in the
  // bucket to clean up because nothing was put there.
  const preview = await makePreview(file);
  if (!preview) throw new Error(noThumbnailMessage(file));

  const { error: upErr } = await supabase.storage
    .from("media")
    // A year, because this object cannot change: the key is a fresh UUID and
    // `upsert: false` means it is never rewritten (0017-v2 §1). Worth nothing
    // on its own — the token is part of the cache key, so long cache metadata
    // only pays inside the URL-reuse window `signMedia` now provides. The two
    // ship together or not at all.
    .upload(path, file, { contentType: file.type, cacheControl: "31536000", upsert: false });
  if (upErr) throw new Error(`Upload failed: ${upErr.message}`);

  // Same tenant segment, so the storage policy of 20260823000500 governs it
  // with no change; same uuid as the original, so a bucket inventory can pair
  // the two without reading Postgres (0017-v2 §2).
  let previewPath: string | null = null;
  if (preview) {
    const ext = preview.file.type === "image/webp" ? "webp" : "jpg";
    const candidate = `${workspaceId}/preview/${path.split("/")[1].replace(/\.[^.]+$/, "")}.${ext}`;
    const { error } = await supabase.storage
      .from("media")
      .upload(candidate, preview.file, {
        contentType: preview.file.type,
        cacheControl: "31536000",
        upsert: false,
      });
    if (error) {
      // The derivative was made and could not be stored — a different failure
      // from "could not be made", and no longer survivable: the insert below
      // would be refused by the not-null constraint and report a column name
      // rather than a cause. Take the original back out on the way past.
      await supabase.storage.from("media").remove([path]);
      throw new Error(`Upload failed while saving the thumbnail: ${error.message}`);
    }
    previewPath = candidate;
  }

  const { data, error } = await supabase
    .from("media_assets")
    .insert({
      workspace_id: workspaceId,
      folder_id: folderId,
      brand_id: brandId,
      storage_path: path,
      original_filename: file.name,
      mime_type: file.type,
      byte_size: file.size,
      width: facts.width,
      height: facts.height,
      duration_seconds: facts.durationSeconds,
      checksum: await checksum(file),
      preview_path: previewPath,
      preview_mime: previewPath ? preview!.file.type : null,
      preview_width: previewPath ? preview!.width : null,
      preview_height: previewPath ? preview!.height : null,
      // The original's size stays in `byte_size`; this is the derivative's, so
      // a preview is metered at what it actually costs.
      preview_bytes: previewPath ? preview!.file.size : null,
    })
    .select("id")
    .single();

  if (error) {
    // The row is what makes the object reachable; without it the upload is
    // dead weight against the workspace's storage quota. Both keys, now: an
    // asset is a set of objects and every cleanup path has to say so.
    await supabase.storage.from("media").remove(previewPath ? [path, previewPath] : [path]);
    throw asAppError(error);
  }
  return data.id;
}

/** Pre-flight against the plan, using the same function the trigger enforces with. */
async function assertUploadAllowed(
  workspaceId: string,
  file: File,
  facts: MediaFacts,
): Promise<void> {
  const checks: Array<[string, number]> = [
    ["max_upload_bytes", file.size],
    ["cloud_storage_bytes", file.size],
  ];
  if (facts.durationSeconds) checks.push(["max_video_seconds", facts.durationSeconds]);

  for (const [featureKey, requested] of checks) {
    const { data, error } = await supabase.rpc("check_entitlement", {
      p_workspace_id: workspaceId,
      p_feature: featureKey,
      p_requested: requested,
    });
    if (error) throw asAppError(error);

    const row = Array.isArray(data) ? data[0] : data;
    if (row && row.allowed === false) {
      // Shaped exactly like the database's own PT402, so one handler covers both.
      throw asAppError({
        message: `This file exceeds your ${row.plan_code} plan.`,
        code: "PT402",
        details: JSON.stringify({
          feature: featureKey,
          feature_name: null,
          reason: row.reason ?? "over_limit",
          plan: row.plan_code,
          limit: row.limit_value,
          used: row.used,
          requested,
          unit: featureKey === "max_video_seconds" ? "seconds" : "bytes",
        }),
      });
    }
  }
}

/**
 * `post_media`, as every caller wants it: in order, with the deleted files
 * dropped.
 *
 * PostgREST returns an embedded collection in whatever order the planner
 * produced, so sorting is the caller's job and doing it in one place is how the
 * carousel avoids being shuffled differently on each screen. A null
 * `media_assets` is a file deleted from the library since — expected on a
 * published post (migration 20260828000900), impossible on one still waiting to
 * go out.
 */
export function postMedia(post: Post): NonNullable<Post["post_media"]> {
  return (post.post_media ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .filter((m) => m.media_assets);
}

/**
 * The foreign key each `posts` <-> `media_assets` embed travels, named rather
 * than inferred.
 *
 * `post_media` (20260908000100) gives the two tables a second path between
 * them, and PostgREST answers an ambiguous embed with an error rather than a
 * guess — a break that would land on the media library and the calendar at
 * once, with a message about relationships that names neither feature. Naming
 * the constraint is a promise that this asks for the COVER, which is what every
 * thumbnail on those screens draws. The whole set goes through `post_media`
 * explicitly, and that path is unambiguous because it is spelled out.
 */
const COVER = "media_assets!posts_media_asset_id_fkey";

export async function createPost(args: {
  workspaceId: string;
  kind: PostKind;
  /**
   * Every photo, in the order they will be seen. One entry is an ordinary
   * post; two to ten is an Instagram carousel and a Facebook multi-photo post.
   * The database refuses an eleventh, and refuses more than one of anything
   * that is not a photo.
   */
  mediaAssetIds: string[];
  caption: string;
  title: string | null;
  connectionIds: string[];
  scheduledAt: string | null;
  youtubePrivacyStatus: YouTubePrivacy;
  whatsapp?: { requestId: string; revision: number; brandId: string };
  /** Submit this draft post (fills the same row) instead of creating one. */
  draftPostId?: string;
}): Promise<Post> {
  if (args.draftPostId) {
    const { data, error } = await supabase.rpc("submit_post_draft", {
      p_post_id: args.draftPostId,
      p_kind: args.kind,
      p_caption: args.caption,
      p_title: args.title,
      p_connection_ids: args.connectionIds,
      p_scheduled_at: args.scheduledAt,
      p_youtube_privacy_status: args.youtubePrivacyStatus,
      p_media_asset_ids: args.mediaAssetIds,
    });
    if (error) throw asAppError(error);
    return data as Post;
  }
  const { data, error } = await supabase.rpc(args.whatsapp ? "create_post_from_whatsapp" : "create_post", {
    ...(args.whatsapp ? { p_request_id: args.whatsapp.requestId, p_revision: args.whatsapp.revision, p_brand_id: args.whatsapp.brandId } : {}),
    p_workspace_id: args.workspaceId,
    p_kind: args.kind,
    // The cover, and the first slide — sent as well as the list because the
    // parameter is still required and still what every thumbnail reads.
    p_media_asset_id: args.mediaAssetIds[0],
    p_caption: args.caption,
    p_title: args.title,
    p_connection_ids: args.connectionIds,
    p_scheduled_at: args.scheduledAt,
    p_youtube_privacy_status: args.youtubePrivacyStatus,
    p_media_asset_ids: args.mediaAssetIds,
  });
  // asAppError keeps the PT402 signal a plain Error would discard, so the
  // caller can offer an upgrade instead of showing a database message.
  if (error) throw asAppError(error);
  return data as Post;
}

export async function listPosts(workspaceId: string): Promise<Post[]> {
  const { data, error } = await supabase
    .from("posts")
    // `post_media(sort_order)` and not the assets: the list draws no pictures,
    // it only needs to say "3 photos", and pulling ten joined rows per post to
    // count them would be fifty times the payload for a number.
    .select(`*, ${COVER}(id, storage_path, mime_type, byte_size, workspace_id, preview_path, preview_bytes), post_media(sort_order), deliveries(*, social_connections(display_name, avatar_url))`)
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw new Error(error.message);
  return data as Post[];
}

/** Review Post: release a held post to the scheduler. */
export async function approvePost(postId: string): Promise<void> {
  const { error } = await supabase.rpc("approve_post", { p_post_id: postId });
  if (error) throw asAppError(error);
}

/** Review Post: send it back to Draft; its unattempted deliveries are removed. */
export async function rejectPost(postId: string, note: string | null): Promise<void> {
  const { error } = await supabase.rpc("reject_post", { p_post_id: postId, p_note: note });
  if (error) throw asAppError(error);
}

export async function getPost(postId: string): Promise<Post> {
  const { data, error } = await supabase
    .from("posts")
    .select(
      `*, ${COVER}(*), post_media(sort_order, media_assets(*)),` +
      " deliveries(*, social_connections(display_name, avatar_url))",
    )
    .eq("id", postId)
    .single();
  if (error) throw new Error(error.message);
  // Two levels of embed is past what the generated PostgREST types model, so
  // the row shape is asserted rather than inferred — the same trade `listMedia`
  // already makes.
  return data as unknown as Post;
}

/**
 * Delete a post.
 *
 * It does NOT take the post off the networks it has already gone out to. That is
 * this product's scope, not a platform limit — both networks document deletion,
 * and neither is reachable from here today: Instagram's needs
 * `instagram_manage_contents`, which this app does not request, and works only
 * on the Facebook Login path [C053][C054]; Facebook's is covered by the
 * `pages_manage_posts` we already hold but is restricted to "select developers"
 * at the endpoint [C055][C056]. See migration 20260908000300.
 *
 * The caller must have said so before getting here — `still_live` in the reply
 * is the database's own answer to "was any of it published", so a confirmation
 * written from it cannot disagree with what actually happened.
 *
 * An RPC rather than `.delete()`, because deleting a post that the executor has
 * claimed this minute would publish it to a live audience with no local record
 * that it happened. The database holds a lock across that check
 * (20260908000300).
 */
export async function deletePost(postId: string): Promise<{
  deliveries: number;
  published_to: number;
  still_live: boolean;
}> {
  const { data, error } = await supabase.rpc("delete_post", { p_post_id: postId });
  if (error) throw asAppError(error);
  return data as { deliveries: number; published_to: number; still_live: boolean };
}

/**
 * Move a post to a different time — the drag on the calendar, and the "Move to"
 * field beside it.
 *
 * An RPC, not an UPDATE, for three reasons the migration spells out: the write
 * policy on `posts` speaks of `post.edit_own`/`post.edit_any` while this is
 * `post.schedule`, the executor takes its due time from `deliveries`
 * (`next_attempt_at`) rather than from the post, and the check has to hold a
 * lock against the cron tick that could claim a delivery mid-move.
 *
 * Plan refusals come back as PT402, so `isPaywall` handles a post dragged past
 * the plan's scheduling horizon exactly as Compose already handles one created
 * past it.
 */
export async function reschedulePost(postId: string, when: Date): Promise<Post> {
  const { data, error } = await supabase.rpc("reschedule_post", {
    p_post_id: postId,
    p_scheduled_at: when.toISOString(),
  });
  if (error) throw asAppError(error);
  return data as Post;
}

/**
 * Take one network off a post.
 *
 * On a destination that has NOT gone out this cancels it: the post will never
 * be sent there. On one that HAS, it removes our record of a post that stays
 * live — see `DeleteDeliveryDialog` for the sentence the user reads, and
 * migration 20260908000500 for why the record is preserved in the audit trail
 * rather than merely lost.
 *
 * An RPC because `deliveries` has no client write policy at all: every change
 * to one goes through a function that can check the per-destination permission
 * and hold a lock against the cron tick. Removing the last destination is
 * refused there — a post with none recomputes to `draft` having been published.
 */
export async function deleteDelivery(deliveryId: string): Promise<{
  was_published: boolean;
  account: string | null;
  remaining: number;
  post_status: Post["status"];
}> {
  const { data, error } = await supabase.rpc("delete_delivery", {
    p_delivery_id: deliveryId,
  });
  if (error) throw asAppError(error);
  return data as {
    was_published: boolean;
    account: string | null;
    remaining: number;
    post_status: Post["status"];
  };
}

export async function retryDelivery(deliveryId: string): Promise<Delivery> {
  const { data, error } = await supabase.rpc("retry_delivery", { p_delivery_id: deliveryId });
  if (error) throw new Error(error.message);
  return data as Delivery;
}

/**
 * The media library: everything uploaded to this workspace, newest first, with
 * the posts each asset was used in.
 *
 * `posts` embeds through `posts.media_asset_id`, so one round trip answers both
 * "what have I uploaded" and "where did it go" — the second question being the
 * reason the library exists rather than a bare file list.
 */
/**
 * Which drawer of the library to read (ADR-0018).
 *
 * `folderIds` is a list rather than one id because "this client, including
 * every shoot under it" is the normal request, and the subtree is already known
 * in the browser from the folder tree — asking for the descendants here costs
 * one `in (...)` instead of a second recursive query per page.
 */
export type MediaScope =
  | { kind: "all" }
  | { kind: "unfiled" }
  | { kind: "folders"; folderIds: string[] };

export async function listMedia(
  workspaceId: string,
  limit = 60,
  // A page at a time, because storage is the point now: a library of hundreds
  // of files must not silently stop at whatever the first request happened to
  // fetch. Callers that only need a picker's worth leave this alone.
  offset = 0,
  scope: MediaScope = { kind: "all" },
  /** The brand being viewed (ADR-0029), or null for every brand. A SECOND axis,
   *  ANDed with the folder scope rather than replacing it: "labtest1's Aug
   *  shoot" is one question, and folders alone cannot ask it. */
  brandId: string | null = null,
): Promise<MediaAsset[]> {
  // Two ways a file reaches a post since carousels (20260908000100): as the
  // cover, through `posts.media_asset_id`, and as any other slide, through
  // `post_media`. Asking only the first would tell somebody that nine photos
  // out of every ten-photo post had never been used — on the one screen whose
  // job is to answer "where did this go".
  const POST_FIELDS =
    "id, kind, caption, title, status, scheduled_at, created_at," +
    " deliveries(id, platform, status, provider_url, error_message)";

  let query = supabase
    .from("media_assets")
    .select(
      `*, posts!posts_media_asset_id_fkey(${POST_FIELDS}),` +
      ` post_media(posts(${POST_FIELDS}))`,
    )
    .eq("workspace_id", workspaceId);

  if (scope.kind === "unfiled") query = query.is("folder_id", null);
  // An empty folder list would mean "no filter" to PostgREST, which is the
  // opposite of what an empty selection means here.
  if (scope.kind === "folders") {
    if (scope.folderIds.length === 0) return [];
    query = query.in("folder_id", scope.folderIds);
  }
  if (brandId) query = query.eq("brand_id", brandId);

  const { data, error } = await query
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);
  if (error) throw asAppError(error);
  // The embedded select is beyond what the generated PostgREST types model, so
  // the row shape is asserted here rather than inferred.
  //
  // The two paths are merged back into the single `posts` array the library has
  // always read, deduplicated by id: a file that is both the cover and a later
  // slide of the same post is one use of it, not two.
  return (data as unknown as Array<
    MediaAsset & { post_media?: Array<{ posts: NonNullable<MediaAsset["posts"]>[number] | null }> }
  >).map(({ post_media, ...asset }) => {
    const seen = new Map((asset.posts ?? []).map((p) => [p.id, p]));
    for (const row of post_media ?? []) {
      if (row.posts && !seen.has(row.posts.id)) seen.set(row.posts.id, row.posts);
    }
    return { ...asset, posts: [...seen.values()] };
  });
}

/**
 * Pull a file into the library from a cloud drive, without it passing through
 * this device. The URL is the short-lived, pre-authenticated download link a
 * picker hands back — we never hold a token for the drive itself.
 */
export async function importMedia(args: {
  workspaceId: string;
  url: string;
  filename?: string | null;
  source?: string | null;
  folderId?: string | null;
  brandId?: string | null;
}): Promise<MediaAsset> {
  const { asset } = await callFunction<{ asset: MediaAsset }>("media-import", {
    workspace_id: args.workspaceId,
    url: args.url,
    filename: args.filename ?? null,
    source: args.source ?? null,
    folder_id: args.folderId ?? null,
    brand_id: args.brandId ?? null,
  });
  return asset;
}

// ---------------------------------------------------------------- dashboard

/**
 * The whole overview in one call.
 *
 * The timezone is the browser's, sent rather than assumed: without it a post
 * published at 11pm in Kolkata lands on yesterday's bar. The window is clamped
 * server-side — this is the only place that names the default.
 */
export async function loadDashboard(
  workspaceId: string,
  days = 30,
): Promise<DashboardData> {
  let timezone = "UTC";
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    // An engine without a resolved timezone gets UTC days, which is wrong by
    // hours rather than wrong by a day for most of the world.
  }
  const { data, error } = await supabase.rpc("workspace_dashboard", {
    p_workspace_id: workspaceId,
    p_days: days,
    p_tz: timezone,
  });
  if (error) throw asAppError(error);
  return data as DashboardData;
}

// ------------------------------------------------------------------ folders
//
// A folder is metadata and nothing else (ADR-0018): no bytes move when one is
// renamed, and no permission follows from being in one.

export async function listMediaFolders(workspaceId: string): Promise<MediaFolder[]> {
  const { data, error } = await supabase.rpc("media_folder_tree", {
    p_workspace_id: workspaceId,
  });
  if (error) throw asAppError(error);
  return (data ?? []) as MediaFolder[];
}

/**
 * The two numbers the folder sidebar shows that no folder row can: everything,
 * and everything nobody has filed yet. Counted by the database rather than by
 * measuring the page, which is paged and would report the page size.
 */
export async function mediaCounts(
  workspaceId: string,
): Promise<{ total: number; unfiled: number }> {
  const [all, unfiled] = await Promise.all([
    supabase.from("media_assets").select("id", { count: "exact", head: true })
      .eq("workspace_id", workspaceId),
    supabase.from("media_assets").select("id", { count: "exact", head: true })
      .eq("workspace_id", workspaceId).is("folder_id", null),
  ]);
  return { total: all.count ?? 0, unfiled: unfiled.count ?? 0 };
}

export async function createMediaFolder(
  workspaceId: string,
  name: string,
  parentId: string | null = null,
): Promise<{ id: string }> {
  const { data, error } = await supabase
    .from("media_folders")
    .insert({ workspace_id: workspaceId, name: name.trim(), parent_id: parentId })
    .select("id")
    .single();
  // 23505 is the case-insensitive sibling-name index. The database sentence for
  // it names an index; this one names the problem.
  if (error) {
    if (error.code === "23505") {
      throw new Error(`There is already a folder called "${name.trim()}" here.`);
    }
    throw asAppError(error);
  }
  return data as { id: string };
}

export async function renameMediaFolder(folderId: string, name: string): Promise<void> {
  const { data, error } = await supabase
    .from("media_folders")
    .update({ name: name.trim() })
    .eq("id", folderId)
    .select("id");
  if (error) {
    if (error.code === "23505") {
      throw new Error(`There is already a folder called "${name.trim()}" here.`);
    }
    throw asAppError(error);
  }
  // RLS filters rather than raising, so a refused rename returns no rows and
  // would otherwise look like it worked until the next refresh.
  if (!data || data.length === 0) {
    throw new Error("You do not have permission to change folders in this workspace.");
  }
}

export async function moveMediaFolder(
  folderId: string,
  parentId: string | null,
): Promise<void> {
  const { data, error } = await supabase
    .from("media_folders")
    .update({ parent_id: parentId })
    .eq("id", folderId)
    .select("id");
  if (error) throw asAppError(error);
  if (!data || data.length === 0) {
    throw new Error("You do not have permission to change folders in this workspace.");
  }
}

/**
 * Remove a folder. `withContents` moves what is inside up to the parent first —
 * it never deletes a file, which is a different permission and a different
 * button.
 */
export async function deleteMediaFolder(
  folderId: string,
  withContents = false,
): Promise<void> {
  const { error } = await supabase.rpc("delete_media_folder", {
    p_folder_id: folderId,
    p_with_contents: withContents,
  });
  if (error) throw asAppError(error);
}

/**
 * Put files in a brand, or take them out of every brand with null (ADR-0029).
 *
 * A plain UPDATE, like `moveMediaAssets` and unlike `set_connection_brand`.
 * `media_assets` has had an update policy since 20260823000500 and files
 * already move between folders this way; a connection does not, because that
 * row points at a credential.
 */
export async function setMediaBrand(
  assetIds: string[],
  brandId: string | null,
): Promise<void> {
  if (assetIds.length === 0) return;
  const { data, error } = await supabase
    .from("media_assets")
    .update({ brand_id: brandId })
    .in("id", assetIds)
    .select("id");
  if (error) throw asAppError(error);
  if (!data || data.length === 0) {
    throw new Error("You do not have permission to file files in this workspace.");
  }
}

/** File (or unfile, with null) assets in one statement. */
export async function moveMediaAssets(
  assetIds: string[],
  folderId: string | null,
): Promise<void> {
  if (assetIds.length === 0) return;
  const { data, error } = await supabase
    .from("media_assets")
    .update({ folder_id: folderId })
    .in("id", assetIds)
    .select("id");
  if (error) throw asAppError(error);
  if (!data || data.length === 0) {
    throw new Error("You do not have permission to move files in this workspace.");
  }
}

/**
 * Remove an asset from the library, bytes and all.
 *
 * A storage quota nobody can free is a trap, so this is the other half of
 * uploading — and since migration 20260828000900 a file that has already been
 * published can go. `posts.media_asset_id` is `on delete set null`: the post
 * keeps its caption, its timings and its per-network outcome, and loses only
 * the preview of bytes that now live on the platform it was published to.
 *
 * A post that has NOT gone out still needs the file to publish at all, so the
 * database refuses that one (P0001) with a sentence naming how many posts are
 * in the way. Both that and the 23503 fallback are shown as they arrive.
 *
 * Row first, object second. The other order can leave a row pointing at bytes
 * that are gone — a broken thumbnail forever. This order can at worst leave an
 * unreferenced object, which costs storage until it is swept and nothing else.
 */
export async function deleteMedia(
  asset: Pick<MediaAsset, "id" | "storage_path" | "preview_path">,
): Promise<void> {
  // `select()` so the outcome is a row count, not a silence. RLS filters rather
  // than raising: without `media.delete` the statement succeeds having matched
  // nothing, and reporting that as a deletion would be a lie the user only
  // discovers when the file is still there after a refresh.
  const { data, error } = await supabase
    .from("media_assets").delete().eq("id", asset.id).select("id");
  if (error) {
    // Anything still pointing at the file with `on delete restrict` — nothing
    // does today — would otherwise arrive as a constraint name.
    if (error.code === "23503") {
      throw new Error(
        "Something is still using this file, so it cannot be deleted yet.",
      );
    }
    throw asAppError(error);
  }
  if (!data || data.length === 0) {
    throw new Error("You do not have permission to delete files in this workspace.");
  }
  // Best effort: the row is what makes the object reachable, and RLS already
  // proved the caller may delete it. A failure here is a sweeping job's problem,
  // not something to show as "delete failed" after the file has visibly gone.
  // An asset is a set of keys since derivatives (0017-v2 §2), and the preview
  // is as much dead weight as the original once the row is gone.
  const keys = asset.preview_path ? [asset.storage_path, asset.preview_path] : [asset.storage_path];
  await supabase.storage.from("media").remove(keys);
  // The objects are gone; cached URLs for them are 404s waiting to be drawn.
  forgetSignedUrls(keys);
}

/** One asset, for composing a post from something already uploaded. */
export async function getMedia(assetId: string): Promise<MediaAsset> {
  const { data, error } = await supabase
    .from("media_assets")
    .select("*")
    .eq("id", assetId)
    .single();
  if (error) throw asAppError(error);
  return data as unknown as MediaAsset;
}

/**
 * What `signMedia` can be given.
 *
 * A bare path still works — some callers genuinely only have one. Passing the
 * asset itself is what lets the transfer be metered (ADR-0017), and is the
 * reason every caller that holds the row now passes the row.
 */
/** A key plus what it weighs and whose it is — everything the meter needs. */
export type SignedAssetTarget = {
  storage_path: string; byte_size?: number | null; workspace_id?: string | null;
};

/** A bare key is still accepted, and is simply unmeterable. Prefer the object. */
export type SignTarget = string | SignedAssetTarget;

const pathOf = (t: SignTarget): string => (typeof t === "string" ? t : t.storage_path);

/**
 * Which screen is about to spend the bytes (0017-v2 §0B).
 *
 * The meter's grain was (day, workspace, person, kind, basis, source), and
 * `source` only says `web` or `mobile` — so a workspace that minted forty
 * thousand URLs in a day was one row that could not say whether somebody
 * browsed a large library or left a calendar open over a weekend. Those need
 * opposite fixes, which is why this exists.
 */
export type EgressSurface = "library" | "calendar" | "post" | "compose" | "picker";

/** Enough of an asset to point at its preview. */
type Previewable = Pick<
  MediaAsset, "storage_path" | "byte_size" | "workspace_id" | "preview_path" | "preview_bytes"
>;

/**
 * The key a small rendering should read (0017-v2 §2), or NOTHING.
 *
 * NULL IS THE POINT. This used to fall back to `storage_path` when an asset had
 * no derivative, and that one `??` was the most expensive line in the product:
 * a two-hundred-pixel grid square would download a thirty-megabyte phone video
 * because the thumbnail had never been made. Measured over the thirty days to
 * 2026-09-19, one workspace holding three such files served 1.53 GB against
 * 61 MB at rest.
 *
 * So a missing derivative now renders as a placeholder and costs nothing. The
 * return type is what enforces it: every caller has to say what it draws when
 * there is no thumbnail, and cannot quietly reach for the original instead.
 *
 * Used at BOTH ends — to decide what to sign, and to look the result back up.
 * Calling it in one place and not the other is how a grid signs derivatives and
 * then renders nothing.
 *
 * A FULL-SIZE VIEW DOES NOT USE THIS. Somewhere that genuinely means to show
 * the original — the media detail pane, a video somebody pressed play on —
 * signs `storage_path` directly and always did.
 */
export function thumbPath(a: Pick<MediaAsset, "preview_path">): string | null {
  return a.preview_path;
}

/**
 * The same choice, as something `signMedia` can meter honestly.
 *
 * The size travels with the path: metering a 40 KB thumbnail at the original's
 * four megabytes would report that derivatives changed nothing, which is the
 * one number this work is judged by.
 *
 * Null for an asset with no derivative, so nothing is minted for it at all —
 * the saving is the mint as much as the transfer, because an attributed mint is
 * recorded at the object's full size whether or not the bytes ever move.
 */
export function thumbTarget(a: Previewable): SignedAssetTarget | null {
  return a.preview_path
    ? { storage_path: a.preview_path, byte_size: a.preview_bytes, workspace_id: a.workspace_id }
    : null;
}

/** The thumbnails among a list of assets, ready to sign. See `thumbTarget`. */
export function thumbTargets(assets: Previewable[]): SignedAssetTarget[] {
  return assets.map(thumbTarget).filter((t): t is SignedAssetTarget => t !== null);
}

/**
 * Bytes we are about to hand out a URL for.
 *
 * ATTRIBUTED, NEVER MEASURED — the distinction the meter is built around. The
 * browser fetches the object directly from Storage with this URL and nothing we
 * run observes that request: it may be served from cache, ranged, or abandoned.
 * So this records the object's full size as an UPPER BOUND on what the mint
 * cost, and `platform_usage` reports it in its own column that is never added
 * to the measured one.
 *
 * Today that upper bound is close to the truth, and ADR-0017 says why: a signed
 * URL is a new URL every time, so the browser cache is keyed on a string that
 * never repeats and the CDN in front of Storage sees a unique request for an
 * object that has not changed. When step 2 of that record lands and URLs start
 * being reused, this number should fall away from the bill — and watching it do
 * that is most of the point of measuring it.
 *
 * Fire-and-forget. A library grid must not wait on a meter, and must not fail
 * because one was unavailable.
 */
function meterPreviews(targets: SignTarget[], surface: EgressSurface | "unknown"): void {
  const byWorkspace = new Map<string, { bytes: number; objects: number }>();

  for (const target of targets) {
    if (typeof target === "string") continue;   // no size, nothing honest to record
    const workspaceId = target.workspace_id;
    const bytes = target.byte_size ?? 0;
    if (!workspaceId || bytes <= 0) continue;
    const entry = byWorkspace.get(workspaceId) ?? { bytes: 0, objects: 0 };
    entry.bytes += bytes;
    entry.objects += 1;
    byWorkspace.set(workspaceId, entry);
  }

  for (const [workspaceId, { bytes, objects }] of byWorkspace) {
    void supabase
      .rpc("record_egress", {
        p_workspace_id: workspaceId,
        p_kind: "signed_url",
        p_basis: "attributed",
        p_bytes: bytes,
        p_objects: objects,
        p_source: "web",
        p_surface: surface,
      })
      .then(({ error }) => {
        // Swallowed on purpose, and not surfaced: a reader looking at their
        // media library has no action to take about a usage meter, and an
        // error banner over a working grid would be noise.
        if (error) console.warn("egress not recorded:", error.message);
      });
  }
}

// ------------------------------------------------- signed URL reuse (0017-v2 §1)
//
// A signed URL is a new URL every time it is minted, so the token is part of
// the CDN cache key and a re-mint is a guaranteed cache miss for an object that
// cannot have changed. That is what made two status polls expensive: Calendar
// re-signed its whole window every 30 seconds and PostDetail every 5, and each
// new string was a `src` the browser had never seen.
//
// IN MEMORY, PER TAB, AND DELIBERATELY NOT PERSISTED. ADR-0017 §2 proposed the
// same `localStorage` convention as `simino.workspace`; 0017-v2 §1 reverses it.
// A workspace id is a preference. A signed URL is bearer authority over
// customer media — ADR-0005 forbids persisting it to the database for exactly
// that reason, and durable, origin-scoped, XSS-readable storage is the same
// concession wearing different clothes. A reload re-signs. That is the cost.
//
// Evicted by `forgetSignedUrls`: on sign-out, on workspace change, and when an
// asset is deleted. Expiry alone is not invalidation — a cached response for
// one exact URL can outlive the token that minted it, so deletion of the object
// is the only reliable global revocation (0017-v2 §1).

/** Re-sign this long before expiry, so a URL never dies mid-render. */
const SIGNED_URL_SAFETY_MS = 5 * 60_000;

const signedUrls = new Map<string, { url: string; expiresAt: number }>();

/**
 * Drop cached URLs — all of them, or just these paths.
 *
 * Called from the workspace provider when the signed-in user or the active
 * workspace changes, and from `deleteMedia` for the object that just went.
 */
export function forgetSignedUrls(paths?: string[]): void {
  if (!paths) { signedUrls.clear(); return; }
  for (const path of paths) signedUrls.delete(path);
}

/** A cached URL with enough life left to hand out, or nothing. */
function liveUrl(path: string): string | undefined {
  const hit = signedUrls.get(path);
  if (!hit) return undefined;
  if (hit.expiresAt - Date.now() <= SIGNED_URL_SAFETY_MS) {
    signedUrls.delete(path);
    return undefined;
  }
  return hit.url;
}

/**
 * Previews for private objects (ADR-0005). Signed in a batch because a library
 * page would otherwise make one round trip per thumbnail.
 *
 * Reuses a live URL for a path already signed in this tab, so calling this
 * again for an unchanged object costs no round trip, no new cache key, and no
 * attributed transfer. Every caller gets that without changing: a screen may go
 * on asking for what it wants to draw, and only a genuinely new or
 * nearly-expired path reaches Storage.
 *
 * Duplicates collapse. The same asset on four scheduled posts is one signed URL
 * and one metered transfer — Calendar deduplicated for that reason and now
 * every caller does.
 *
 * `expiresIn` is honoured on a mint, not on a hit: a caller asking for a longer
 * TTL than a cached URL has left is given the cached one. Nothing in the app
 * varies it today, and a caller that genuinely needs a longer life should
 * `forgetSignedUrls` that path first.
 */
export async function signMedia(
  targets: SignTarget[],
  options: { surface?: EgressSurface; expiresIn?: number } = {},
): Promise<Map<string, string>> {
  const { surface = "unknown", expiresIn = 3600 } = options;
  const out = new Map<string, string>();
  if (targets.length === 0) return out;

  const need: SignTarget[] = [];
  const asked = new Set<string>();
  for (const target of targets) {
    const path = pathOf(target);
    if (asked.has(path)) continue;
    asked.add(path);
    const live = liveUrl(path);
    if (live) out.set(path, live);
    else need.push(target);
  }
  if (need.length === 0) return out;

  const { data, error } = await supabase.storage
    .from("media")
    .createSignedUrls(need.map(pathOf), expiresIn);
  if (error) return out;   // Thumbnails are a nicety; the list still works.

  const expiresAt = Date.now() + expiresIn * 1000;
  for (const row of data ?? []) {
    if (!row.signedUrl || !row.path) continue;
    out.set(row.path, row.signedUrl);
    signedUrls.set(row.path, { url: row.signedUrl, expiresAt });
  }

  // After the mint and only for what was actually minted: a failed signing
  // handed out nothing and must not appear as transfer, and a cache hit is not
  // a transfer at all.
  meterPreviews(need.filter((t) => out.has(pathOf(t))), surface);
  return out;
}

/** Deliveries whose bytes must come from this browser (ADR-0004). */
export async function youtubeDeliveriesFor(postId: string): Promise<{ id: string }[]> {
  const { data, error } = await supabase
    .from("deliveries")
    .select("id")
    .eq("post_id", postId)
    .eq("platform", "youtube")
    .is("provider_post_id", null);
  if (error) throw new Error(error.message);
  return data ?? [];
}

// ---------------------------------------------------------------- drafts
//
// The seam between the AI studio and Compose (docs/AI_STUDIO_PLAN.md §2). These
// live here rather than inside the studio module on purpose: `content_drafts`
// is the one table both sides touch, and Compose must be able to read it
// without importing anything from a module it does not depend on.
//
// None of this is gated on the AI plan. Saving a caption you typed and picking
// it again later is a plain content feature; the paywall is on generating.

/** Newest first, optionally narrowed to what Compose is asking for. */
export async function listDrafts(
  workspaceId: string,
  kind?: DraftKind,
  limit = 50,
): Promise<ContentDraft[]> {
  let q = supabase
    .from("content_drafts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (kind) q = q.eq("kind", kind);

  const { data, error } = await q;
  if (error) throw asAppError(error);
  return data as ContentDraft[];
}

export async function saveDraft(args: {
  workspaceId: string;
  kind: DraftKind;
  body: string;
  source?: "ai" | "manual";
  generationId?: string | null;
  mediaAssetId?: string | null;
}): Promise<ContentDraft> {
  const { data, error } = await supabase
    .from("content_drafts")
    .insert({
      workspace_id: args.workspaceId,
      kind: args.kind,
      body: args.body,
      source: args.source ?? "ai",
      generation_id: args.generationId ?? null,
      media_asset_id: args.mediaAssetId ?? null,
    })
    .select("*")
    .single();
  if (error) throw asAppError(error);
  return data as ContentDraft;
}

/**
 * Many at once, which is a different operation and not a loop over `saveDraft`.
 *
 * One statement, so an import either lands or does not: five hundred separate
 * inserts can stop halfway and leave a library half-filled with no record of
 * where it stopped, and the user's only recovery is to find the boundary by
 * eye. RLS still runs per row, so a workspace the caller may not write to is
 * refused here exactly as it would be one at a time.
 */
export async function saveDrafts(args: {
  workspaceId: string;
  kind: DraftKind;
  bodies: string[];
  source: DraftSource;
}): Promise<ContentDraft[]> {
  if (args.bodies.length === 0) return [];
  const { data, error } = await supabase
    .from("content_drafts")
    .insert(args.bodies.map((body) => ({
      workspace_id: args.workspaceId,
      kind: args.kind,
      body,
      source: args.source,
    })))
    .select("*");
  if (error) throw asAppError(error);
  return data as ContentDraft[];
}

/**
 * One page of them, with the total, because a library worth paginating is a
 * library nobody can hold in their head.
 *
 * The count is asked for on the same request rather than inferred from a short
 * last page: "page 3 of 4" is only true if something counted, and a caller that
 * guesses gets it wrong exactly when the page size divides the total.
 */
export async function listDraftsPage(args: {
  workspaceId: string;
  kind: DraftKind;
  page: number;
  pageSize: number;
  /** Case-insensitive substring of the body. Empty means everything. */
  search?: string;
}): Promise<{ rows: ContentDraft[]; total: number }> {
  const from = args.page * args.pageSize;
  let q = supabase
    .from("content_drafts")
    .select("*", { count: "exact" })
    .eq("workspace_id", args.workspaceId)
    .eq("kind", args.kind);

  if (args.search?.trim()) {
    // `%` and `,` end a PostgREST filter early; escape before they become one.
    const term = args.search.trim().replace(/[%,\\]/g, "\\$&");
    q = q.ilike("body", `%${term}%`);
  }

  const { data, count, error } = await q
    .order("created_at", { ascending: false })
    .range(from, from + args.pageSize - 1);
  if (error) throw asAppError(error);
  return { rows: (data ?? []) as ContentDraft[], total: count ?? 0 };
}

/**
 * Just the text, for the one question that needs every row: is this caption
 * already here? Selecting bodies alone keeps a duplicate check on a library of
 * two thousand from pulling two thousand full rows into a browser.
 */
export async function listDraftBodies(
  workspaceId: string,
  kind: DraftKind,
  limit = 5000,
): Promise<string[]> {
  const { data, error } = await supabase
    .from("content_drafts")
    .select("body")
    .eq("workspace_id", workspaceId)
    .eq("kind", kind)
    .limit(limit);
  if (error) throw asAppError(error);
  return (data ?? []).map((r: { body: string }) => r.body);
}

/**
 * Rewrite the words. `source` is deliberately untouched: it records where the
 * text came from, and fixing a typo in an imported caption does not make it
 * stop having been imported.
 */
export async function updateDraft(draftId: string, body: string): Promise<ContentDraft> {
  const { data, error } = await supabase
    .from("content_drafts")
    .update({ body })
    .eq("id", draftId)
    .select("*")
    .single();
  if (error) throw asAppError(error);
  return data as ContentDraft;
}

export async function deleteDraft(draftId: string): Promise<void> {
  const { error } = await supabase.from("content_drafts").delete().eq("id", draftId);
  if (error) throw asAppError(error);
}

// ---------------------------------------------------------------- brand details
//
// The values captions refer to as `{{key}}`. Read by anyone who may see a
// draft — a caption whose tokens cannot be resolved is a caption nobody can
// read — and written by anyone who may make one.
//
// Every row in the workspace comes back in one read, both the brand-specific
// ones and the shared ones, and `effectiveDetails` picks the set that applies
// to whichever brand is being looked at. A query per brand would mean a round
// trip on every switch of the brand selector, for a table that holds a dozen
// rows.

export async function listBrandDetails(workspaceId: string): Promise<BrandDetail[]> {
  const { data, error } = await supabase
    .from("brand_details")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("sort_order", { ascending: true })
    .order("key", { ascending: true });
  if (error) throw asAppError(error);
  return data as BrandDetail[];
}

/**
 * Create or replace by name, within one brand — or within the workspace when
 * `brandId` is null, which is the value every brand falls back to.
 *
 * An RPC rather than an upsert: uniqueness is now two partial indexes (one per
 * scope) and PostgREST's `on_conflict` has nowhere to put the WHERE clause that
 * would make the conflict target resolvable. See 20260918000300.
 */
export async function saveBrandDetail(args: {
  workspaceId: string;
  brandId: string | null;
  key: string;
  label: string;
  value: string;
  sortOrder?: number;
}): Promise<BrandDetail> {
  const { data, error } = await supabase.rpc("save_brand_detail", {
    p_workspace_id: args.workspaceId,
    p_brand_id: args.brandId,
    p_key: args.key,
    p_label: args.label,
    p_value: args.value,
    p_sort_order: args.sortOrder ?? 100,
  });
  if (error) throw asAppError(error);
  return data as BrandDetail;
}

export async function deleteBrandDetail(id: string): Promise<void> {
  const { error } = await supabase.from("brand_details").delete().eq("id", id);
  if (error) throw asAppError(error);
}

// ---------------------------------------------------------------- calendar
//
// The publishing calendar asks a different question of the same table Posts
// does: not "what have I made", but "what goes out, and when". A post
// sits on the calendar at `scheduled_at` when it has one, and at `created_at`
// when it does not — a draft made on Tuesday belongs on Tuesday until somebody
// gives it a time.

/**
 * Everything landing in `[from, to)`, with the media it publishes and where it
 * is going.
 *
 * The window is the grid the browser is about to draw — a month view asks for
 * six weeks, not for a month — so paging is not the calendar's problem. The cap
 * is a safety net for a workspace that publishes hundreds of times a week.
 */
export async function listCalendarPosts(
  workspaceId: string,
  from: Date,
  to: Date,
  limit = 500,
): Promise<Post[]> {
  const lo = from.toISOString();
  const hi = to.toISOString();
  const select =
    `*, ${COVER}(id, storage_path, mime_type, byte_size, workspace_id,` +
    ` preview_path, preview_bytes),` +
    " deliveries(*, social_connections(display_name, avatar_url))";

  // Two queries rather than one `or(...)`, in parallel: a timestamp inside a
  // PostgREST `or` has to be hand-quoted, because the parser splits the group on
  // the same commas and dots the value contains. These two filters are disjoint
  // by construction — a post either has a release time or it does not — so the
  // union needs no de-duplication, and every value is escaped by the client
  // rather than by us.
  const [timed, untimed] = await Promise.all([
    supabase.from("posts").select(select)
      .eq("workspace_id", workspaceId)
      .gte("scheduled_at", lo).lt("scheduled_at", hi)
      .order("scheduled_at", { ascending: true })
      .limit(limit),
    supabase.from("posts").select(select)
      .eq("workspace_id", workspaceId)
      .is("scheduled_at", null)
      .gte("created_at", lo).lt("created_at", hi)
      .order("created_at", { ascending: true })
      .limit(limit),
  ]);
  if (timed.error) throw asAppError(timed.error);
  if (untimed.error) throw asAppError(untimed.error);

  // The embedded select is beyond what the generated PostgREST types model.
  return [
    ...(timed.data ?? []),
    ...(untimed.data ?? []),
  ] as unknown as Post[];
}
